// @ts-check
// Owns only dialog state; the PDP trigger selects it by `data-config-id`.

import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';
import { EDIT_LINE_PARAM } from '@theme/custom.customizer-cart-edit';
import { waitForChatWidget } from '@theme/custom.chat-widget';
import {
  prepareKeyboardViewport,
  restoreKeyboardViewport,
} from '@theme/custom.customizer-keyboard';

/** @typedef {import('@theme/custom.chat-widget').ChatWidget} ChatWidget */

/**
 * @extends DialogComponent
 */
class CustomizerOverlay extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.customizerOpen, this.#handleOpen);
    // The native `close` event covers every way out (close button, ESC, click-outside), so the
    // viewport meta swapped on open can never leak past the overlay.
    this.refs.dialog.addEventListener('close', restoreKeyboardViewport);
    this.refs.dialog.addEventListener('close', this.#restoreChat);

    // Edit restores asynchronously; defer opening to avoid flashing a blank wizard.
    const params = new URLSearchParams(window.location.search);
    if (params.get('customizer') === 'open' && !params.get(EDIT_LINE_PARAM)) {
      queueMicrotask(() =>
        this.#handleOpen(
          new CustomEvent(ThemeEvents.customizerOpen, {
            detail: { configId: this.dataset.configId },
          })
        )
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerOpen, this.#handleOpen);
    this.refs.dialog.removeEventListener('close', restoreKeyboardViewport);
    this.refs.dialog.removeEventListener('close', this.#restoreChat);
    restoreKeyboardViewport();
  }

  /** Whether the wizard wants the chat widget out of its way: set on open, cleared on close. */
  #hidingChat = false;

  /** @type {ChatWidget | null} The widget this open hid, so the close restores only that. */
  #hiddenChat = null;

  /** A cart edit or deep link opens the overlay on page load, before the embed loads the widget. */
  async #hideChat() {
    this.#hidingChat = true;
    const widget = await waitForChatWidget(() => this.#hidingChat);
    if (!widget || !this.#hidingChat) return;
    widget.hide();
    this.#hiddenChat = widget;
  }

  #restoreChat = () => {
    this.#hidingChat = false;
    this.#hiddenChat?.restore();
    this.#hiddenChat = null;
  };

  /**
   * Open this instance when a trigger requests its config.
   * @param {Event} event - A `CustomizerOpenEvent` carrying `{ configId }`.
   */
  #handleOpen = (event) => {
    const { detail } = /** @type {CustomEvent} */ (event);
    if (detail?.configId !== this.dataset.configId) return;
    // Swapped BEFORE the keyboard can ever show, so the browser never has to change resize
    // behavior mid-animation (see @theme/custom.customizer-keyboard).
    prepareKeyboardViewport();
    this.showDialog();
    void this.#hideChat();
    // Cleared here, not on the request event: the dialog fades in, so dropping the boot scrim
    // earlier would bare the product page. No-op on the PDP path (custom.customizer-boot.liquid).
    delete document.documentElement.dataset.customizerBooting;
  };

  /** Close the overlay. Bound via `on:click='custom-customizer-overlay/close'`. */
  close = () => {
    void this.closeDialog();
  };

  /** Phase 1 marks the CTA busy; a failed add must leave the error visible in the overlay. */
  beginCommit() {
    const { dialog } = this.refs;
    dialog.dataset.committing = '';
    dialog.setAttribute('aria-busy', 'true');
  }

  /** Phase 1 undone — the add failed or timed out. The overlay stays open and usable. */
  cancelCommit() {
    const { dialog } = this.refs;
    delete dialog.dataset.committing;
    dialog.removeAttribute('aria-busy');
  }

  /**
   * Phase 2 closes into the drawer so its top-layer dim never covers the transition.
   * Reduced motion and cart pages use the stock short fade.
   * @param {boolean} [intoDrawer] - Use the stretched handoff only when a drawer opens behind it.
   * @returns {Promise<void>}
   */
  async completeCommit(intoDrawer = false) {
    const { dialog } = this.refs;
    // The shopper closed the overlay (ESC / close button / click-outside) while the add was in
    // flight — there is nothing left to hand off, and the drawer opens on its own.
    if (!dialog.open) {
      this.cancelCommit();
      return;
    }

    if (intoDrawer && !prefersReducedMotion()) dialog.dataset.handoff = '';
    // Held THROUGH the exit, not cleared before it: dropping it here would snap the CTA back from
    // its busy label to price + label mid-fade, and it keeps the button inert through the close.
    await this.closeDialog();
    delete dialog.dataset.handoff;
    this.cancelCommit();
  }
}

if (!customElements.get('custom-customizer-overlay')) {
  customElements.define('custom-customizer-overlay', CustomizerOverlay);
}
